/**
 * MonthlyReportPage - Monthly coffee and rewards report for Bula Coffee Club members
 * Shows monthly stats for shares, points, coupons, and coffee pass usage
 */
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Coffee, Gift, Camera, Calendar, Share2, ChevronLeft, ChevronRight,
  Loader2, Star, TrendingUp, Facebook, Instagram, MessageCircle, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import api from '@/utils/apiClient';

// Month names
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Platform icons
const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  google: Star,
  tripadvisor: MapPin,
  whatsapp: MessageCircle
};

// Stat Card component
const StatCard = ({ icon: Icon, label, value, subtext, color = 'primary' }) => (
  <div className="bg-card border border-border rounded-xl p-4 text-center">
    <div className={`w-10 h-10 mx-auto rounded-xl bg-${color}/10 dark:bg-${color}/20 flex items-center justify-center mb-2`}>
      <Icon className={`w-5 h-5 text-${color}`} />
    </div>
    <p className="text-2xl font-bold text-foreground">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
    {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
  </div>
);

export default function MonthlyReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('user_id');
  
  const now = new Date();
  const [month, setMonth] = useState(parseInt(searchParams.get('month')) || now.getMonth() + 1);
  const [year, setYear] = useState(parseInt(searchParams.get('year')) || now.getFullYear());
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  
  // Load monthly report
  useEffect(() => {
    if (!userIdFromUrl) {
      setLoading(false);
      return;
    }
    
    const loadReport = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/viral/reports/monthly/${userIdFromUrl}?month=${month}&year=${year}`);
        setReport(res.data);
      } catch (err) {
        console.error('Failed to load report:', err);
        toast.error('Failed to load monthly report');
      }
      setLoading(false);
    };
    
    loadReport();
  }, [userIdFromUrl, month, year]);
  
  // Navigate months
  const goToPrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };
  
  const goToNextMonth = () => {
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    
    // Don't go beyond current month
    if (year > currentYear || (year === currentYear && month >= currentMonth)) {
      return;
    }
    
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };
  
  // Check if can navigate to next month
  const canGoNext = () => {
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    return !(year > currentYear || (year === currentYear && month >= currentMonth));
  };
  
  // Share report
  const shareReport = async () => {
    const coffeeText = report?.coffee_pass ? ` ${report.coffee_pass.coffees_claimed} FREE coffees, ${report.coffee_pass.savings} saved!` : '';
    const shareData = {
      title: `My ${MONTHS[month - 1]} ${year} Coffee Report`,
      text: `My Bula Coffee Club ${MONTHS[month - 1]} stats:${coffeeText} ${report?.shares?.total || 0} shares, ${report?.points?.earned || 0} points earned! Join at bulaccinocafe.com/bula-coffee-club`,
      url: 'https://bulaccinocafe.com/bula-coffee-club'
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        navigator.clipboard?.writeText(shareData.text + ' ' + shareData.url);
        toast.success('Copied to clipboard!');
      }
    } else {
      navigator.clipboard?.writeText(shareData.text + ' ' + shareData.url);
      toast.success('Copied to clipboard!');
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // No user ID
  if (!userIdFromUrl || !report) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center">
        <Calendar className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-xl font-heading mb-2">Report Not Found</h1>
        <p className="text-muted-foreground mb-4">
          Access your monthly reports from your account dashboard.
        </p>
        <Link to="/bula-coffee-club">
          <Button>
            <Coffee className="w-4 h-4 mr-2" /> Get Coffee Pass
          </Button>
        </Link>
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{`${MONTHS[month - 1]} ${year} Report | Bulaccino Cafe`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-20">
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-4 py-6">
          <div className="max-w-lg mx-auto">
            <Link 
              to={`/rewards?user_id=${userIdFromUrl}`}
              className="text-sm opacity-80 hover:opacity-100 flex items-center gap-1 mb-4"
            >
              <ChevronLeft className="w-4 h-4" /> Back to Dashboard
            </Link>
            <h1 className="text-xl font-heading">Monthly Report</h1>
            <p className="text-sm opacity-80">{report.name}'s coffee journey</p>
          </div>
        </div>
        
        <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between bg-card border border-border rounded-xl p-4">
            <Button variant="ghost" size="icon" onClick={goToPrevMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <p className="text-lg font-heading text-foreground">{MONTHS[month - 1]}</p>
              <p className="text-sm text-muted-foreground">{year}</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={goToNextMonth}
              disabled={!canGoNext()}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Coffee Pass Section (if linked) */}
          {report.coffee_pass && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-6 text-white"
            >
              <div className="flex items-center gap-2 mb-4">
                <Coffee className="w-5 h-5" />
                <h3 className="font-heading">Bula Coffee Pass</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-4xl font-bold">{report.coffee_pass.coffees_claimed}</p>
                  <p className="text-sm opacity-90">Coffees Claimed</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold">{report.coffee_pass.savings}</p>
                  <p className="text-sm opacity-90">Value This Month</p>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              icon={Camera}
              label="Shares"
              value={report.shares?.total || 0}
              color="purple-500"
            />
            <StatCard
              icon={TrendingUp}
              label="Points Earned"
              value={report.points?.earned || 0}
              color="blue-500"
            />
            <StatCard
              icon={Gift}
              label="Coupons Used"
              value={report.coupons_used || 0}
              color="green-500"
            />
            <StatCard
              icon={Star}
              label="Referrals"
              value={report.referrals || 0}
              color="amber-500"
            />
          </div>
          
          {/* Platform Breakdown */}
          {report.shares?.by_platform && Object.keys(report.shares.by_platform).length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="font-heading text-sm mb-3 text-foreground">Shares by Platform</h3>
              <div className="space-y-2">
                {Object.entries(report.shares.by_platform).map(([platform, count]) => {
                  const Icon = PLATFORM_ICONS[platform] || Share2;
                  return (
                    <div key={platform} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm capitalize text-foreground">{platform}</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Share Button */}
          <Button onClick={shareReport} className="w-full">
            <Share2 className="w-4 h-4 mr-2" /> Share This Report
          </Button>
          
          {/* View Annual Wrapped */}
          <div className="text-center">
            <Link 
              to={`/bula-wrapped?user_id=${userIdFromUrl}&year=${year}`}
              className="text-sm text-primary hover:underline"
            >
              View {year} Bula Wrapped
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
